import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
    Menu, User, LayoutGrid, Store, ShieldCheck, LogOut, ShoppingBag, Scale, ShieldAlert, GitCompareArrows, Briefcase, HelpCircle, Rocket, X, Workflow,
} from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { LogoLockup, LogoMark } from "@/components/LogoMark";

const navLinks = [
    { to: "/auctions", label: "Browse", icon: LayoutGrid },
    { to: "/compare", label: "Why OmniAuc", icon: GitCompareArrows },
    { to: "/how-it-works", label: "How It Works", icon: Workflow },
    { to: "/business", label: "For Business", icon: Briefcase },
    { to: "/trust-safety", label: "Trust & Safety", icon: ShieldCheck },
    { to: "/faq", label: "FAQ", icon: HelpCircle },
];

function Brand() {
    return (
        <Link to="/" data-testid="brand-logo" className="flex items-center">
            <LogoLockup size={34} />
        </Link>
    );
}

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [betaOpen, setBetaOpen] = useState(() => {
        try { return localStorage.getItem("omniauc_beta_dismissed") !== "1"; } catch { return true; }
    });

    const dismissBeta = () => {
        setBetaOpen(false);
        try { localStorage.setItem("omniauc_beta_dismissed", "1"); } catch { /* ignore */ }
    };

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-background">
            {betaOpen && (
                <div className="relative z-50 border-b border-brass/30 bg-brass/10" data-testid="beta-banner">
                    <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-10 py-2 text-center sm:px-6">
                        <Rocket className="h-3.5 w-3.5 shrink-0 text-brass" />
                        <p className="text-xs font-medium text-foreground sm:text-sm">
                            OmniAuc is currently in Beta &mdash; thanks for being an early user.
                        </p>
                        <button
                            onClick={dismissBeta}
                            data-testid="beta-banner-dismiss"
                            aria-label="Dismiss beta banner"
                            className="absolute right-3 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-brass/20 hover:text-foreground sm:right-6"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
            <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                    <div className="flex items-center gap-8">
                        <Brand />
                        <nav className="hidden items-center gap-1 md:flex">
                            {navLinks.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                                    className={({ isActive }) =>
                                        `rounded-md px-3 py-2 text-sm font-medium transition-[background-color,color] duration-150 hover:bg-muted ${isActive ? "text-foreground" : "text-muted-foreground"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    <div className="hidden items-center gap-2 md:flex">
                        {user ? (
                            <>
                                <NotificationBell />
                                <Button asChild variant="secondary" className="rounded-[10px]" data-testid="nav-sell-cta">
                                    <Link to="/dashboard/seller"><Store className="mr-1.5 h-4 w-4" />Sell</Link>
                                </Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="rounded-[10px]" data-testid="nav-account-menu">
                                            <User className="mr-1.5 h-4 w-4" />{user.name?.split(" ")[0] || "Account"}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild><Link to="/dashboard/buyer" data-testid="menu-buyer-dashboard"><ShoppingBag className="mr-2 h-4 w-4" />Buyer dashboard</Link></DropdownMenuItem>
                                        <DropdownMenuItem asChild><Link to="/dashboard/seller" data-testid="menu-seller-dashboard"><Store className="mr-2 h-4 w-4" />Seller dashboard</Link></DropdownMenuItem>
                                        <DropdownMenuItem asChild><Link to="/disputes" data-testid="menu-disputes"><Scale className="mr-2 h-4 w-4" />Disputes</Link></DropdownMenuItem>
                                        <DropdownMenuItem asChild><Link to="/account/verify" data-testid="menu-verify"><ShieldCheck className="mr-2 h-4 w-4" />Verification</Link></DropdownMenuItem>
                                        {user.is_admin && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><ShieldAlert className="mr-2 h-4 w-4 text-electric" />Admin console</Link></DropdownMenuItem>
                                            </>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : (
                            <>
                                <Button asChild variant="ghost" className="rounded-[10px]" data-testid="nav-login"><Link to="/login">Log in</Link></Button>
                                <Button asChild className="rounded-[10px]" data-testid="nav-signup"><Link to="/signup">Create account</Link></Button>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-1 md:hidden">
                        {user && <NotificationBell />}
                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger"><Menu className="h-5 w-5" /></Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72">
                                <div className="mt-6 flex flex-col gap-2">
                                    {navLinks.map((l) => (
                                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">{l.label}</Link>
                                    ))}
                                    <div className="my-2 h-px bg-border" />
                                    {user ? (
                                        <>
                                            <Link to="/dashboard/buyer" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Buyer dashboard</Link>
                                            <Link to="/dashboard/seller" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Seller dashboard</Link>
                                            <Link to="/disputes" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Disputes</Link>
                                            <Link to="/account/verify" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Verification</Link>
                                            {user.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium text-electric hover:bg-muted">Admin console</Link>}
                                            <button onClick={() => { setOpen(false); handleLogout(); }} className="rounded-md px-3 py-2 text-left text-sm hover:bg-muted">Log out</button>
                                        </>
                                    ) : (
                                        <>
                                            <Link to="/login" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Log in</Link>
                                            <Link to="/signup" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Create account</Link>
                                        </>
                                    )}
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>

            <main><Outlet /></main>

            <footer className="mt-16 border-t border-border bg-card">
                <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-12 sm:px-6 md:grid-cols-4">
                    <div className="col-span-2 md:col-span-1">
                        <Brand />
                        <p className="mt-3 max-w-xs text-sm text-muted-foreground">Sealed bids. One shot. Fair outcomes. A privacy-preserving auction marketplace.</p>
                    </div>
                    <div>
                        <h4 className="mb-3 font-display text-sm font-semibold">Marketplace</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/auctions" className="hover:text-foreground">Browse auctions</Link></li>
                            <li><Link to="/compare" className="hover:text-foreground">Why OmniAuc</Link></li>
                            <li><Link to="/business" className="hover:text-foreground">For Business</Link></li>
                            <li><Link to="/dashboard/seller" className="hover:text-foreground">Sell an item</Link></li>
                            <li><Link to="/dashboard/buyer" className="hover:text-foreground">My bids</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="mb-3 font-display text-sm font-semibold">Trust</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/trust-safety" className="hover:text-foreground">Trust & Safety</Link></li>
                            <li><Link to="/faq" className="hover:text-foreground">FAQ</Link></li>
                            <li><Link to="/disputes" className="hover:text-foreground">Dispute resolution</Link></li>
                            <li><Link to="/account/verify" className="hover:text-foreground">Account verification</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="mb-3 font-display text-sm font-semibold">Legal</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/legal/terms" className="hover:text-foreground">Terms of Service</Link></li>
                            <li><Link to="/legal/privacy" className="hover:text-foreground">Privacy Policy</Link></li>
                            <li><Link to="/legal/auction-rules" className="hover:text-foreground">Auction Rules</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-border py-4 text-center text-xs text-muted-foreground">
                    © {new Date().getFullYear()} OmniAuc. All rights reserved.
                </div>
            </footer>
        </div>
    );
}
